import { sub } from "date-fns";
import { produce } from "immer";
import pick from "lodash/pick";
import {
  ApiCtx,
  call,
  ensure,
  Ok,
  resource,
  run,
  safe,
  select,
  sleep,
  spawn,
  take,
  takeEvery,
  takeLeading,
  updateStore,
  useAbortSignal,
} from "starfx";
import { VFile } from "vfile";

import eventEmitter from "@app/contexts/emitter";
import i18n from "@app/i18n";
import { KeyMarker } from "@app/state/constants";
import { schemaB } from "@app/stateCommon/schemaBlue";
import {
  callValue,
  identityResult,
  isErr,
  isResultLikeErr,
  ResultLike,
  safeApi,
  unpack,
} from "@app/statefx/helpers";
import { ResultLikeToResult } from "@app/statefx/helpers/basic";
import { spvReadInvoiceTree } from "@app/statefx/routines/file-application-ops";
import { schema } from "@app/statefx/schema";
import { selectShardsOfInterval } from "@app/statefx/selectors/ds-swr";
import {
  nProcessSpvMessages,
  xapiSpvReadFullTree,
} from "@app/statefx/xThunks/n-file-sys-opts";
import { EnuAppOps } from "@app/types/insider";

import { sysSeparator } from "../helpers/dirTree";
import { callExec, runThunk, safeLast, safeThunk } from "../helpers/fxhelpers";
import { recacheKeys } from "../routines/bootcommander";
import { updateDirOfDownloadCode } from "../routines/file-application-ops";
import { invoiceSubir } from "../selectors/uiSpvMain.sel";
import { xThunk, xWorker } from "../xapis";
import { applog } from "./app-log";
import { queryShards } from "./ds";
import { sfxNotify } from "./notify";
import { aquireLock, releaseLock } from "./prc-lock";

import type { TSChemaBlue } from "@app/stateCommon/schemaBlue";
import type { TFSDirTreeItem } from "../helpers/dirTree";
import type { TKarma, TRepoSPVMessages } from "@app/types/insider";
import type { ThunkCtx } from "@app/types";
import type { Next } from "starfx";
import type { Result } from "starfx";
import type { Operation } from "starfx";
const db = schema;
const t = i18n.t.bind(i18n);

let processController: AbortController;
let res = new VFile(); //
import type { TExecution } from "@app/types/insider";
export type TValidationBreakdown = {
  error: number;
  success: number;
  unknown: number;
  ignored: number;
  unIgnoredErrors: number;
};

const CONST_TESTING_ERROR = "CONST_TESTING_ERROR";
const CONST_NO_ZIP_AVAILABLE = "CONST_NO_ZIP_AVAILABLE";
const CONST_BAD_ZIP = "CONST_BAD_ZIP";
const CONST_NO_XML_AFTER_PROCESSING = "CONST_NO_XML_AFTER_PROCESSING";
const CONST_NO_CONTRACTOR_WHEN_PROCESSING =
  "CONST_NO_CONTRACTOR_WHEN_PROCESSING";
const CONST_BAD_UNZIP = "CONST_BAD_UNZIP";
const CONST_ERROR_ON_STRINGIFY_CONTRACTOR =
  "CONST_ERROR_ON_STRINGIFY_CONTRACTOR";
const CONST_INBOX_REJECT = "CONST_INBOX_REJECT";
const CONST_INBOX_REJECT_FIX = "CONST_INBOX_REJECT_FIX";
const CONST_BAD_ZIP_FIX = "CONST_BAD_ZIP_FIX";
type TSPVErrorMessageKey =
  | typeof CONST_NO_ZIP_AVAILABLE
  | typeof CONST_BAD_ZIP
  | typeof CONST_NO_XML_AFTER_PROCESSING
  | typeof CONST_NO_CONTRACTOR_WHEN_PROCESSING
  | typeof CONST_TESTING_ERROR
  | typeof CONST_BAD_UNZIP
  | typeof CONST_ERROR_ON_STRINGIFY_CONTRACTOR
  | typeof CONST_INBOX_REJECT
  | typeof CONST_INBOX_REJECT_FIX
  | typeof CONST_BAD_ZIP_FIX;

const friendlyMessage = (msg: TSPVErrorMessageKey): string => {
  switch (msg) {
    case CONST_NO_ZIP_AVAILABLE:
      return t("no_zip_available");
    case CONST_BAD_ZIP:
      return t("bad_zip");
    case CONST_NO_XML_AFTER_PROCESSING:
      return t("no_xml_after_processing");
    case CONST_NO_CONTRACTOR_WHEN_PROCESSING:
      return t("no_contractor_when_processing");
    case CONST_TESTING_ERROR:
      return t("testing_error");
    case CONST_BAD_UNZIP:
      return t("bad_unzip");
    case CONST_ERROR_ON_STRINGIFY_CONTRACTOR:
      return t("error_on_stringify_contractor");
    case CONST_INBOX_REJECT:
      return t("inbox_reject");
    case CONST_INBOX_REJECT_FIX:
      return t("inbox_reject_force");
    case CONST_BAD_ZIP_FIX:
      return t("bad_zip_force");

    default:
      return msg;
  }
};

function* dirTreePatch(msg: TRepoSPVMessages, items: TFSDirTreeItem[]) {
  const { QMY_CIF } = msg as TRepoSPVMessages;
  const cif = QMY_CIF.replace("RO", "").trim();
  const subDir = invoiceSubir(msg);
  yield* commonTreeSliceSliceUpdate(
    `${sysSeparator}${cif}${sysSeparator}${subDir}`,
    items
  );
}

function* commonTreeSliceSliceUpdate(
  payload: string,
  itemList: TFSDirTreeItem[]
) {
  const [, cif, invoice] = payload.split(sysSeparator);
  const fstree = yield* select(schema.fsDirTree.select);
  const newState = produce(fstree, (draft) => {
    if (!draft[cif]) {
      draft[cif] = {};
    }
    if (!draft[cif][invoice]) {
      draft[cif][invoice] = [];
    }
    draft[cif][invoice] = itemList;
    return draft;
  });
  console.log("newState", newState);
  yield* call(schema.update(schema.fsDirTree.set(newState)));
}

export const downloadAndUnpack = xThunk.create<TRepoSPVMessages>(
  "/xthnk/spvMessageFileDownloadUnpack1",
  { supervisor: takeLeading },
  function* (ctx: ThunkCtx, next: Next) {
    const fResult = yield* updateDirOfDownloadCode(JSON.stringify(ctx.payload));
    console.log("fResult", fResult);
    if (isErr(fResult)) {
      // yield* runThunk(sfxNotify, {
      //   message: fResult.error.message,
      //   type: 'error',
      //   duration: 3000,
      //   position: 'bottom',
      // });
      yield* next(); // the question //
      return;
    }

    const { QMY_CIF } = ctx.payload as TRepoSPVMessages;
    const cif = QMY_CIF.replace("RO", "").trim();
    const subDir = yield* call(() => invoiceSubir(ctx.payload));

    if (process.env.REACT_APP_MODE === "REACT") {
      yield* safeApi(
        xapiSpvReadFullTree,
        `${sysSeparator}${cif}${sysSeparator}${subDir}`
      );
    } else {
      const dirContent = yield* spvReadInvoiceTree({
        CIFNORO: cif,
        INVOICE: subDir,
      });
      const prsDirContent = yield* safeLast(() => identityResult(dirContent));
      if (isErr(prsDirContent)) {
        console.error("prsDirContent.error", prsDirContent.error);
        yield* next();
        return;
      }
      yield* call(
        dirTreePatch(ctx.payload, prsDirContent.value.items as TFSDirTreeItem[])
      );
    }
    fResult.value = `${fResult.value}{${KeyMarker}${ctx.payload.FXM_ID}}`;
    ctx.result = fResult;
    yield* next();
  }
);
// export const aquireLock = function* (
//   op: 'build' | 'reload' | 'deploy' | 'cache',
// ): Operation<string> {
//   // generate random unique string: ... yeah. why?
//   const newLock = `$:${op}:>${nanoid()}`;
//   yield* call(() =>
//     window.fx.run(function* () {
//       yield* updateStore(db.lock.set(newLock));
//     }),
//   );
//   return newLock;
// };

// export const releaseLock = function* (): Operation<void> {
//   console.log('releaseLock');
//   yield* call(() =>
//     window.fx.run(function* () {
//       yield* updateStore(db.lock.set(null));
//     }),
//   );
// };

export const processSpvMessages = xThunk.create(
  "thunks/processSpvMessages",
  { supervisor: takeLeading },
  function* (ctx: ThunkCtx, next: Next) {
    // run the compiler
    const compiler = yield* execute;
    yield* call(() => compiler);
    // prevent mutation ? (doesn't work)
    ctx.result = Ok(res);
    yield* next();
  }
);

export const abortProcess = xThunk.create(
  "thunks/abortProcess",
  { supervisor: takeEvery },
  function* (ctx: ThunkCtx, next: Next) {
    yield* runThunk(applog, {
      operation: EnuAppOps.spv_msg_abort,
      value: "aborted",
    });

    processController.abort("user abort");
    yield* next();
  }
);

export const clearExecResutls = xThunk.create(
  "thunks/clearExecResutls",
  { supervisor: takeEvery },
  function* (ctx: ThunkCtx, next: Next) {
    yield* updateStore(db.execution.reset());
    yield* next();
  }
);

function stringBetween(source: string, start: string, end: string): string {
  const startIndex = source.indexOf(start) + start.length;
  const endIndex = source.indexOf(end, startIndex);
  return source.substring(startIndex, endIndex !== -1 ? endIndex : undefined);
}

function parseResult(restr: string) {
  const pcr_1 = unpack(restr) as ResultLike<string>;
  const [message, key] =
    pcr_1.ok === false
      ? pcr_1.error.split(KeyMarker)
      : pcr_1.value.split(KeyMarker);

  return [Number(key), message] as [number, string];
}

function getSomeErrorString(fromResult: Result<any>): string {
  if (isResultLikeErr(fromResult)) {
    return fromResult.error;
  }
  const anyError = JSON.stringify({
    ok: false,
    error: "fail: unknown karma error [#1056]",
  });
  return anyError;
}
/* -------------------------------------------------------------------------- */
/* -------------------------------------------------------------------------- */

export const execute = resource(function* (provide) {
  const delphi = yield* safe(() =>
    window.delphiRequire("Jurnal_Quick")
  ) as Operation<Result<TKarma>>;

  const appModule = callValue(
    delphi,
    i18n.t("cannot_load_delphi_project_module"),
    "terminal"
  );

  // TERMINATION
  // flow.onExit ... or onTerminate //
  const exitSignal = yield* useAbortSignal();
  exitSignal.onabort = function () {
    // we exit but we are not in the context any
    void 0;
  };

  // ABORT CONTROLLER
  processController = new AbortController();
  const { signal } = processController;

  const delphiStop = function* (): Operation<void> {
    const haltMessage = yield* callExec(
      () => appModule.efactura.efcmd("stop_process", ""),
      // builder.execute("end_process"),
      "#2",
      ["terminal"]
    ) as Operation<Result<string>>;
    console.log("haltMessage", haltMessage);
  };

  signal.addEventListener("abort", function () {
    run(function* () {
      yield* delphiStop();
    });
  });

  function* advertiseResult(pcr_0: string) {
    yield* sleep(0);
    if ([null, undefined, ""].includes(pcr_0.trim())) {
      return;
    }
    try {
      const pcr_1 = unpack(pcr_0) as ResultLike<string>;
      const [message, key] =
        pcr_1.ok === false
          ? pcr_1.error.split(KeyMarker)
          : pcr_1.value.split(KeyMarker);
      const content = friendlyMessage(message as TSPVErrorMessageKey);
      const parsed =
        pcr_1.ok === false
          ? ({ ok: false, error: content } as ResultLike<string>)
          : ({ ok: true, value: content } as ResultLike<string>);

      const initialExecution = yield* select(schema.execution.selectById, {
        id: key,
      });

      yield* schema.update(
        db.execution.patch({
          [key]: {
            ...initialExecution,
            result: ResultLikeToResult(parsed),
            rawResult: message,
          },
        })
      );
    } catch (error) {
      console.log("error", error);
    }
  }

  const processCompile = function* (
    compile: () => Operation<string>
  ): Operation<void> {
    // we run the compile
    const comResultString = yield* call(compile);
    // return result ot throw error
    // errors on null/undefined
    if (comResultString) {
      return yield* call(advertiseResult(comResultString));
    } else {
      // bad result
      console.log("comResultString.error", comResultString);
    }
  };

  const compiler = function* (): Operation<void> {
    // set current session marker on terminal screen
    eventEmitter.emit("/xthnk/readPdfFile", "start");
    yield* mark(Ok("enter critical section"));
    yield* aquireLock("build");
    res = new VFile();

    try {
      const msg1 = yield* callExec(
        () => appModule.efactura.efcmd("start_process", ""),
        "#2",
        ["terminal"]
      ) as Operation<Result<string>>;
      yield* mark(msg1);

      const uCtrct = yield* select(db.execution.selectTable);
      const spvMsgs = yield* select(db.SPVMessages.selectTable);
      const spvList = pick(spvMsgs, Object.keys(uCtrct));
      yield* mark("the delphi side is ready");
      yield* sleep(300);

      for (const [key, ff] of Object.entries(spvList)) {
        yield* sleep(0);
        const f = ff as TRepoSPVMessages;

        // skip exceptions = ex. ERORI FACTURA  - messages from parsing;
        // if ... continue;
        // const initialMsg = `processing ${f.FXM_ID}`;
        // the execution monitoring //

        const msg = yield* select(schema.execution.selectById, {
          id: f.FXM_ID,
        });

        yield* updateStore(
          db.execution.patch({
            [String(f.FXM_ID)]: {
              ...msg,
              result: Ok("loading"),
            },
          })
        );

        //
        yield* processCompile(function* (): Operation<string> {
          /* -------------------------------------------------------------------------- */
          /// MAIN
          const cr = yield* safe(() =>
            appModule.efactura.efcmd("parse_xml_of_record", JSON.stringify(f))
          ) as Operation<Result<string>>;
          yield* sleep(100);
          // running extra procedures according to the main's outcome
          if (!cr.ok) {
            return getSomeErrorString(cr);
          } else {
            let cmpres = cr.value;

            // this means that if nobody downloaded the file
            // we will do it now
            // do we have a full result with files and everything?
            const [key, message] = parseResult(cr.value);

            const currentFXM = yield* select(db.SPVMessages.selectById, {
              id: key,
            });

            ///FIX ZIP - cowboy move...

            if (
              [CONST_NO_ZIP_AVAILABLE, CONST_BAD_UNZIP, CONST_BAD_ZIP].includes(
                message
              )
            ) {
              yield* advertiseResult(
                JSON.stringify({
                  ok: false,
                  error: `${"in remediere"}${KeyMarker}${key}`,
                })
              );
              const zResult = yield* safe(() =>
                appModule.efactura.efcmd(
                  "fix_bad_zip",
                  JSON.stringify(currentFXM)
                )
              ) as Operation<Result<string>>;
              if (isErr(zResult)) {
                return getSomeErrorString(zResult);
              } else {
                cmpres = zResult.value;
              }
              const currentFXM00 = yield* select(db.SPVMessages.selectById, {
                id: key,
              });
              const cr01 = yield* safe(() =>
                appModule.efactura.efcmd(
                  "parse_xml_of_record",
                  JSON.stringify(currentFXM00)
                )
              ) as Operation<Result<string>>;
              if (!cr01.ok) {
                return getSomeErrorString(cr01);
              }
              return cr01.value;
            }

            /// FIX NAME PARSING side effects already fixed upstream
            // NOTE. MOSTLY THERE IS NO CIF IN XML !!!!!
            if ([CONST_INBOX_REJECT].includes(message)) {
              yield* advertiseResult(
                JSON.stringify({
                  ok: false,
                  error: `${"in remediere"}${KeyMarker}${key}`,
                })
              );
              const currentFXM01 = yield* select(db.SPVMessages.selectById, {
                id: key,
              });
              const ibxReject = yield* safe(() =>
                appModule.efactura.efcmd(
                  "fix_inbox_reject",
                  JSON.stringify(currentFXM01)
                )
              ) as Operation<Result<string>>;

              if (isErr(ibxReject)) {
                return getSomeErrorString(ibxReject);
              } else {
                cmpres = ibxReject.value;
              }

              const currentFXM02 = yield* select(db.SPVMessages.selectById, {
                id: key,
              });
              const cr02 = yield* safe(() =>
                appModule.efactura.efcmd(
                  "parse_xml_of_record",
                  JSON.stringify(currentFXM02)
                )
              ) as Operation<Result<string>>;

              if (!cr02.ok) {
                return getSomeErrorString(cr02);
              }

              return cr02.value;
            }

            /// FIX BLOB IN DB (VFILES) -- side effects already fixed upstream
            const currentFXM04 = yield* select(db.SPVMessages.selectById, {
              id: key,
            });
            // will be redone anyway
            if (currentFXM04.FXM_ID && !currentFXM04.FSH_ID) {
              const fshInRecord = yield* safe(() =>
                appModule.efactura.efcmd(
                  "fix_missing_fsh",
                  JSON.stringify(currentFXM04)
                )
              ) as Operation<Result<string>>;

              if (isErr(fshInRecord)) {
                return getSomeErrorString(fshInRecord);
              } else {
                cmpres = fshInRecord.value;
              }
              const currentFXM05 = yield* select(db.SPVMessages.selectById, {
                id: key,
              });
              const cr05 = yield* safe(() =>
                appModule.efactura.efcmd(
                  "parse_xml_of_record",
                  JSON.stringify(currentFXM05)
                )
              ) as Operation<Result<string>>;

              if (!cr05.ok) {
                return getSomeErrorString(cr05);
              }
              return cr05.value;
            }

            ///FIX BAD REFERENCES -side effects already fixed upstream
            const currentFXM06 = yield* select(db.SPVMessages.selectById, {
              id: key,
            });
            if (
              !currentFXM06.REFERINTA_FFID ||
              !currentFXM06.REFERINTA_DATAFFID
            ) {
              const refInRecord = yield* safe(() =>
                appModule.efactura.efcmd(
                  "fix_bad_references",
                  JSON.stringify(currentFXM06)
                )
              ) as Operation<Result<string>>;
              if (isErr(refInRecord)) {
                return getSomeErrorString(refInRecord);
              } else {
                cmpres = refInRecord.value;
              }
              const currentFXM07 = yield* select(db.SPVMessages.selectById, {
                id: key,
              });
              const cr07 = yield* safe(() =>
                appModule.efactura.efcmd(
                  "parse_xml_of_record",
                  JSON.stringify(currentFXM07)
                )
              ) as Operation<Result<string>>;
              if (!cr07.ok) {
                return getSomeErrorString(cr07);
              }
              return cr07.value;
            }

            return cmpres;
          }
        });
      }
      yield* mark(Ok("build done"));
    } finally {
      // on screen..
      yield* runThunk(applog, {
        operation: EnuAppOps.spv_msg_end,
        value: "dataset_processed",
      });
      yield* call(releaseLock);
      eventEmitter.emit("/xthnk/readPdfFile", "done");
    }
  };

  const compilers = yield* compiler();
  try {
    yield* provide(() => compilers);
  } finally {
    res = new VFile();
  }
});

/* -------------------------------------------------------------------------- */
/* -------------------------------------------------------------------------- */
export function* mark(
  result: Result<string> | string,
  prev = ""
): Operation<void> {
  yield* call(() => {
    return result;
  });
}

/* -------------------------------------------------------------------------- */
/*                            READ NEW SPV MESSAGES                           */
/* -------------------------------------------------------------------------- */
function hasApiUrl(obj: any): obj is { apiUrl: string } {
  return "apiUrl" in obj;
}

function isEmpty(found: TExecution): boolean {
  //
  return !found || Object.keys(found).length === 0;
}
const spvTables = Object.entries(schemaB)
  .filter(([k, v]) => {
    return (
      hasApiUrl(v) &&
      [
        // 'SPVMessageUserState',
        schemaB.SPVMessageUserState.name,
        // 'fileSysHead',
        schemaB.fileSysHead.name,
        // 'fileSysContent',
        schemaB.fileSysContent.name,
        // 'SPVInvoiceRaw',
        schemaB.SPVInvoiceRaw.name,
        // 'SPVMessageKeys',
        schemaB.SPVMessageKeys.name,
        // 'SPVMessages',
        schemaB.SPVMessages.name,
        // 'contractors_cff',
        schemaB.contractors_cff.name,
        // 'contractors_temp',
        schemaB.contractors_temp.name,
      ].includes(k)
    );
  })
  .map(([_, v]) => v.name) as (keyof TSChemaBlue)[];

function* waitforUnlock() {
  yield* spawn(function* () {
    sleep(4000); // 4 seconds
    yield* runThunk(sfxNotify, {
      message: "Se asteapta finalizarea procesului anterior",
      type: "info",
      duration: 3000,
      position: "bottom",
    });
  });
  const locked = yield* select(db.lock.select);
  if (!locked) {
    return;
  }
  while (true) {
    const action = yield* take("*");
    const locked = yield* select(db.lock.select);
    if (!locked) {
      break;
    }
    if (action.type === "bailout/waitunlock") {
      break;
    }
  }
}
export const fxTriggerSPVMessageReading = xWorker.post<string>(
  "/sfx/wget/receiveSpvmsg",
  { supervisor: takeLeading },
  function* (ctx: ApiCtx, next: Next) {
    // const locked = yield* select(db.lock.select);
    // if (locked) {
    //   // we can message that is work in prg
    //   console.log('locked', locked);
    //   // we can show the popup if is hidden
    //   return;
    // }
    yield* waitforUnlock();

    let CIF = ctx.payload;
    if (!CIF) {
      return;
    }
    CIF = CIF.replace("RO", "").trim();
    if (!CIF) {
      return;
    }
    yield* aquireLock("build");
    yield* ensure(() =>
      window.fx.run(function* () {
        yield* releaseLock();
      })
    );
    yield* updateStore(db.execution.reset());
    yield* schema.update(
      db.notify.set({ message: "Se proceseaza solicitarea" })
    );
    const who = yield* select(db.whoami.select);

    /// for recovery , we need more
    const sharsdOfSelection = yield* select((s) =>
      selectShardsOfInterval(s, {
        start: sub(new Date(), { days: 60 }), //max interval for reading spv messages
        end: new Date(),
      })
    );
    const shards = yield* call(recacheKeys(spvTables, sharsdOfSelection));
    ctx.request = ctx.req({
      method: "POST",
      url: `/spv/processMain?CIF=${CIF}&ama=${who.AmaId}`,
      body: JSON.stringify(shards),
    });
    // yield* safe()''
    yield* updateStore(db.execution.reset());
    yield* sleep(1);
    yield* updateStore(
      db.execution.set({
        [-1]: {
          id: -1,
          filename: "Interogare SPV",
          rawResult: "",
          result: Ok("in desfasurare"),
        },
      })
    );
    yield* next();

    while (true) {
      yield* take("*");
      const found = yield* select(db.execution.selectById, { id: "-1" }); /// -1 IS LOCKING THE FLOW UNTIL THE MSG ARE PREPROCESSED //
      if (!found || isEmpty(found)) {
        break;
      }
    }
    //[findme:#807492024]//
    let outerResult;
    if (process.env.REACT_APP_MODE === "DELPHI") {
      outerResult = yield* safeThunk(processSpvMessages);
    } else {
      outerResult = yield* safeApi(nProcessSpvMessages, "");
    }
    // this is an important call:
    yield* call(queryShards.run(shards));
  }
);
